import React from "react";

interface Props {
  /** null = unlimited; number = capped count per day. */
  value: number | null;
  onChange: (next: number | null) => void;
}

/**
 * Toggle + number input for a menu's per-day stock cap.
 *   Off → unlimited (column stays NULL)
 *   On  → integer 1–9999
 */
const DailyStockEditor: React.FC<Props> = ({ value, onChange }) => {
  const enabled = value != null;
  const safe = enabled && value! >= 1 ? value! : 1;

  return (
    <div className="rounded-2xl bg-ink-50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-ink-1000">Daily stock cap</span>
          <span className="text-xs text-ink-500">
            Off = unlimited. On = sell up to N per day, then auto sold-out.
          </span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? safe : null)}
          />
          <div className="w-11 h-6 bg-ink-300 rounded-full peer-checked:bg-ink-1000 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
        </label>
      </div>

      {enabled && (
        <div className="pt-3 border-t border-ink-200">
          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => onChange(Math.max(1, safe - 1))}
              disabled={safe <= 1}
              className="w-32 h-20 rounded-2xl bg-white border-2 border-ink-200 text-ink-1000 font-display font-black text-2xl leading-none flex items-center justify-center hover:bg-ink-100 disabled:opacity-30 disabled:hover:bg-white"
              aria-label="Decrease"
            >
              −
            </button>
            <div className="flex-1">
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={9999}
                value={safe}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  onChange(Number.isNaN(v) ? 1 : Math.max(1, Math.min(9999, v)));
                }}
                className="w-full h-20 rounded-2xl bg-white border-2 border-ink-200 text-center font-display font-black text-2xl leading-none text-ink-1000 focus:outline-none focus:border-ink-1000"
              />
            </div>
            <button
              type="button"
              onClick={() => onChange(Math.min(9999, safe + 1))}
              disabled={safe >= 9999}
              className="w-32 h-20 rounded-2xl bg-white border-2 border-ink-200 text-ink-1000 font-display font-black text-2xl leading-none flex items-center justify-center hover:bg-ink-100 disabled:opacity-30 disabled:hover:bg-white"
              aria-label="Increase"
            >
              +
            </button>
          </div>
          <p className="mt-2 text-center text-xs text-ink-500">pieces per day</p>
        </div>
      )}
    </div>
  );
};

export default DailyStockEditor;
